/**
 * Boost PFS/SD price corrector for BHD 3-decimal currency.
 *
 * Boost treats BHD as 2-decimal, producing display values 10× too high.
 * This script fetches the authoritative variant price from Shopify's
 * `/products/{handle}.js` and replaces the wrong display.
 *
 * BHD subunit = fils (1 BHD = 1000 fils). Shopify returns prices in minor
 * units, so divide by 1000 for the major-unit value.
 *
 * Strategy: target each `.boost-sd__product-price` directly, walk the DOM
 * upward to find the enclosing product link, fetch that product, and replace
 * the leaf price spans in-place.
 */
(function () {
  'use strict';

  var LOG = false; // flip to true for debug output
  function log() { if (LOG && window.console) console.log.apply(console, ['[boost-price-fix]'].concat([].slice.call(arguments))); }

  var cache = {};
  var pending = {};
  var processed = new WeakSet();

  function formatBHD(minorUnit) {
    if (minorUnit == null || isNaN(minorUnit)) return '';
    // Shopify's /products/{handle}.js returns BHD prices in cents (divide by 100),
    // not fils, because their API normalizes all currencies to 2-decimal.
    // So 900 = 9 BHD. We then pad to 3 decimals for BHD display convention.
    return (Number(minorUnit) / 100).toFixed(3) + ' BHD';
  }

  function getProduct(handle) {
    if (Object.prototype.hasOwnProperty.call(cache, handle)) return Promise.resolve(cache[handle]);
    if (pending[handle]) return pending[handle];
    var p = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { cache[handle] = data; delete pending[handle]; return data; })
      .catch(function () { cache[handle] = null; delete pending[handle]; return null; });
    pending[handle] = p;
    return p;
  }

  // Walk up from the price element to find the product link
  function findProductHandle(priceEl) {
    var node = priceEl;
    for (var depth = 0; depth < 12 && node; depth++) {
      // Check this level or any descendant for a product link
      var link = node.querySelector ? node.querySelector('a[href*="/products/"]') : null;
      if (link) {
        var href = link.getAttribute('href') || '';
        var m = href.match(/\/products\/([^/?#]+)/);
        if (m) return m[1];
      }
      node = node.parentElement;
    }
    return null;
  }

  function getLeafSpans(priceEl) {
    var spans = [];
    priceEl.querySelectorAll('span').forEach(function (s) {
      if (s.children.length === 0 && s.textContent && s.textContent.indexOf('BHD') !== -1) {
        spans.push(s);
      }
    });
    return spans;
  }

  function fixPrice(priceEl) {
    if (processed.has(priceEl)) return;

    var handle = findProductHandle(priceEl);
    if (!handle) { log('no handle for', priceEl); return; }

    getProduct(handle).then(function (product) {
      if (!product || !product.variants || !product.variants.length) { log('no product', handle); return; }

      var variant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
      var price = formatBHD(variant.price);
      var comparePrice =
        variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)
          ? formatBHD(variant.compare_at_price)
          : null;

      var spans = getLeafSpans(priceEl);
      if (spans.length === 0) { log('no leaf spans in', priceEl); return; }

      log('fixing', handle, '→', price, comparePrice);

      if (spans[0].textContent !== price) spans[0].textContent = price;
      if (spans.length > 1 && comparePrice && spans[1].textContent !== comparePrice) {
        spans[1].textContent = comparePrice;
      }
      processed.add(priceEl);
    });
  }

  // Match any Boost-SD element whose class contains "price" — covers
  // .boost-sd__product-price, .boost-sd__search-result-price,
  // .boost-sd__instant-search-product-price, .boost-sd__autocomplete-price, etc.
  var PRICE_EL_SEL = '[class*="boost-sd__"][class*="price"]';

  function fixAll() {
    try {
      // Filter to only outermost price containers (skip nested -wrapper, -content children
      // which would walk up to the same product link)
      var seen = new WeakSet();
      document.querySelectorAll(PRICE_EL_SEL).forEach(function (el) {
        // Skip if any ancestor matching the same selector has already been processed
        var p = el.parentElement;
        var hasParentMatch = false;
        while (p) {
          if (p.matches && p.matches(PRICE_EL_SEL) && seen.has(p)) {
            hasParentMatch = true;
            break;
          }
          p = p.parentElement;
        }
        if (hasParentMatch) return;
        seen.add(el);
        fixPrice(el);
      });
    } catch (e) { log('fixAll error', e); }
  }

  var observerInstalled = false;
  function installObserver() {
    if (observerInstalled) return;
    observerInstalled = true;
    var observer = new MutationObserver(function (mutations) {
      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' &&
            (mutations[i].addedNodes.length || mutations[i].removedNodes.length)) {
          relevant = true;
          break;
        }
      }
      if (!relevant) return;

      if (observer._pending) return;
      observer._pending = true;
      Promise.resolve().then(function () {
        observer._pending = false;
        fixAll();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    fixAll();
    installObserver();
    [100, 300, 600, 1200, 2500, 5000].forEach(function (ms) { setTimeout(fixAll, ms); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', fixAll);

  // Expose for manual debugging if needed
  window.__boostPriceFix = { fixAll: fixAll, cache: cache, toggleLog: function () { LOG = !LOG; return LOG; } };
})();
